import React, { useEffect, useState } from "react";

import { BusinessHoursInput, EventClickArg } from "@fullcalendar/core";
import { DateClickArg } from "@fullcalendar/interaction";
import { useDispatch, useSelector } from "react-redux";

import { CalendarEvent } from "@ds-developer/calendar";

import {
  calendarActions,
  selectCalendarInitialDate,
  selectCalendarView,
  selectSelectedEventId,
} from "@hubcapp/state";

import { EventType } from "@ds-developer/models";
import { mapEventsToCalendarEvents } from "@ds-developer/calendar";

import {
  AddLessonModal,
  EditTimeOffModal,
} from "@hubcapp/components/Calendar/components";

import { ViewLessonModal } from "@hubcapp/components/Calendar/components/Modals/ViewLessonModal";
import {
  constructWorkingDays,
  constructWorkingHours,
  getCalenderEventsQueryInput,
  getEarliestStartTime,
} from "./helpers";
import { useAuth } from "@hubcapp/context";
import Calendar from "./Calendar";
import useQueryCalendarEvents from "./hooks/useQueryCalendarEvents";

import { useTealium } from "@hubcapp/context";

const StateProvider = () => {
  const dispatch = useDispatch();
  const selectedEventId = useSelector(selectSelectedEventId);
  const view = useSelector(selectCalendarView);
  const initialDate =
    useSelector(selectCalendarInitialDate) || new Date(Date.now());
  const [selectedEventType, setSelectedEventType] = useState<EventType>();
  const [renderEvents, setRenderEvents] = useState<CalendarEvent[]>([]);
  const [workingHours, setWorkingHours] = useState<BusinessHoursInput>();
  const [scrollTime, setScrollTime] = useState<string>();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [showViewLessonModal, setShowViewLessonModal] = useState<boolean>(
    false,
  );
  const {
    instructor: { id: instructorId },
  } = useAuth();
  const { trackEvent } = useTealium();

  const { data, loading, refetch } = useQueryCalendarEvents({
    ...getCalenderEventsQueryInput(initialDate, view),
    instructorId,
  });

  const handleEventClick = ({
    event: { extendedProps: eventProps },
  }: EventClickArg) => {
    // Travel events are not clickable
    if (
      eventProps.eventType === EventType.Travel ||
      eventProps.eventType === EventType.Cancelled
    ) {
      return;
    }

    //Hack to not open modal if map button is clicked
    if ((event?.srcElement as any)?.localName === "button") {
      return;
    }

    trackEvent({
      eventCategory: "Calendar",
      eventAction: "Interaction",
      eventLabel: "View Lesson",
    });

    dispatch(
      calendarActions.calenderEventClicked({
        id: eventProps.id,
      }),
    );

    setShowViewLessonModal(true);
  };

  const handleOnDateClick = (event: DateClickArg) => {
    const { date } = event;

    setSelectedDate(date);
    dispatch(
      calendarActions.calendarShowAddLessonModal({
        showAddLessonModal: true,
      }),
    );
  };

  const handleCloseViewLessonModal = () => {
    trackEvent({
      eventCategory: "Calendar - View Lesson",
      eventAction: "Button Click",
      eventLabel: "Cancel",
    });
    setSelectedDate(undefined);
    setShowViewLessonModal(false);
  };

  useEffect(() => {
    setSelectedEventType(
      renderEvents.find(event => event.extendedProps?.id === selectedEventId)
        ?.extendedProps?.eventType,
    );
  }, [selectedEventId]);

  useEffect(() => {
    dispatch(calendarActions.calendarEventsLoading({ loading }));
  }, [loading]);

  useEffect(() => {
    if (!loading) {
      setRenderEvents(
        data?.instructor?.calendarEvents
          ? mapEventsToCalendarEvents(data.instructor.calendarEvents)
          : [],
      );
      setWorkingHours(constructWorkingHours(data?.instructor?.workingHours));
      setScrollTime(getEarliestStartTime(data?.instructor?.workingHours));
    }
  }, [loading, data, initialDate]);

  return (
    <>
      <AddLessonModal
        selectedDate={selectedDate}
        refetchCalendarData={refetch}
      />
      {selectedEventId &&
        // this is only here to stop page breaking with mock data when event popup is triggered externally (eg from footer)
        selectedEventType &&
        (selectedEventType === EventType.Unavailable ? (
          <EditTimeOffModal />
        ) : showViewLessonModal ? (
          <ViewLessonModal
            closeViewLessonModal={handleCloseViewLessonModal}
            lessonId={selectedEventId}
            open={showViewLessonModal}
            refetchCalendarData={refetch}
          />
        ) : null)}
      <Calendar
        events={renderEvents}
        onEventClick={handleEventClick}
        onDateClick={handleOnDateClick}
        workingHours={workingHours}
        workingDays={constructWorkingDays(workingHours)}
        scrollTime={scrollTime}
        view={view}
      />
    </>
  );
};

export default StateProvider;
